import { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import Swal from "sweetalert2";
import { printTableQr, printTableQrStatus } from "../../../services/table.service";
import { handleServerValidations } from "../../../../../utils/commonfunction";
import { SWAL_SETTINGS } from "../../../../../utils/Constants";

export const PRINT_JOB_POLL_INTERVAL_MS = 2000;
export const PRINT_JOB_POLL_TIMEOUT_MS = 30000;

// Destination -> the restaurant-settings field holding that printer's IP. The
// cashier tablet only claims jobs for roles that have an IP, so a role without
// one is offered disabled instead of queueing a job that can never print.
export const THERMAL_PRINT_TARGETS = [
  { role: "cashier", ipField: "cashier_printer_ip", labelKey: "label_print_to_cashier_printer" },
  { role: "hall", ipField: "hall_printer_ip", labelKey: "label_print_to_hall_printer" },
  { role: "kitchen", ipField: "kitchen_printer_ip", labelKey: "label_print_to_kitchen_printer" },
];

const RESOLVED_STATUSES = ["success", "failed", "dead"];

/**
 * Queues a table's QR code on one of the restaurant's thermal printers.
 *
 * The web app cannot reach a printer: the backend enqueues a print job and a
 * cashier tablet claims it over the LAN. That means "accepted" is not "printed"
 * — the job is polled so the admin is told when paper actually came out, and
 * when nothing is listening.
 */
export const useTableQrPrint = (t) => {
  // Keyed by table as well as role: one hook instance serves the whole table
  // grid, so a role alone would grey out the print button on every OTHER
  // table's drawer while one table's job is still being polled.
  const [printing, setPrinting] = useState(null);
  const restSettingDetails = useSelector((state) => state?.restSettings?.data);
  const pollTimer = useRef(null);

  useEffect(() => () => clearInterval(pollTimer.current), []);

  // `text`, never `title`: SweetAlert2 parses `title` as HTML, and the failure
  // text can carry a printer error string that originated on a tablet.
  const finish = (icon, title, text) => {
    clearInterval(pollTimer.current);
    setPrinting(null);
    Swal.fire({ icon, title, ...(text ? { text } : {}), ...SWAL_SETTINGS });
  };

  const pollJob = (jobId) => {
    let elapsed = 0;
    clearInterval(pollTimer.current);
    pollTimer.current = setInterval(async () => {
      elapsed += PRINT_JOB_POLL_INTERVAL_MS;
      const res = await printTableQrStatus(jobId).catch(() => null);
      const status = res?.success ? res?.data?.status : null;

      if (status === "success") {
        finish("success", t("msg_print_qr_printed"));
      } else if (RESOLVED_STATUSES.includes(status)) {
        finish("error", t("msg_print_qr_failed"), res?.data?.error || undefined);
      } else if (elapsed >= PRINT_JOB_POLL_TIMEOUT_MS) {
        // The job stays queued — a tablet that comes back online will still
        // print it, so this is a warning about the wait, not a failure.
        finish("warning", t("msg_print_qr_still_waiting"));
      }
    }, PRINT_JOB_POLL_INTERVAL_MS);
  };

  const printTo = async (tableId, printerRole) => {
    setPrinting({ tableId });
    const res = await printTableQr({ o_id: tableId, printer_role: printerRole }).catch(() => null);

    // No job id means nothing was queued — polling it would spin for the whole
    // timeout and then report a wait that never started.
    if (!res?.success || !res?.data?.job_id) {
      setPrinting(null);
      // `res` is null only on a network-level failure, and
      // handleServerValidations dereferences its argument — without the
      // fallback it throws inside this un-awaited handler and the click
      // silently does nothing. The message goes in `text` for the same reason
      // finish() does: `title` is parsed as HTML.
      Swal.fire({ icon: "error", text: handleServerValidations(res || {}) || t("msg_print_qr_failed"), ...SWAL_SETTINGS });
      return;
    }

    pollJob(res.data.job_id);
  };

  const targets = THERMAL_PRINT_TARGETS.map((target) => ({
    ...target,
    configured: Boolean(restSettingDetails?.[target.ipField]),
  }));

  return { printing, printTo, targets };
};
